import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const SalaryCalculatorPage = () => {
  const [salary, setSalary] = useState('50000');
  const [salaryType, setSalaryType] = useState('annual');
  const [hoursPerWeek, setHoursPerWeek] = useState('40');
  const [weeksPerYear, setWeeksPerYear] = useState('52');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const amount = parseFloat(salary) || 0;
    const hours = parseFloat(hoursPerWeek) || 40;
    const weeks = parseFloat(weeksPerYear) || 52;
    
    let annual, monthly, biweekly, weekly, daily, hourly;
    
    switch (salaryType) {
      case 'hourly':
        hourly = amount;
        daily = amount * (hours / 5);
        weekly = amount * hours;
        biweekly = weekly * 2;
        monthly = (weekly * weeks) / 12;
        annual = weekly * weeks;
        break;
      case 'daily':
        daily = amount;
        hourly = amount / (hours / 5);
        weekly = amount * 5;
        biweekly = weekly * 2;
        monthly = (weekly * weeks) / 12;
        annual = weekly * weeks;
        break;
      case 'weekly':
        weekly = amount;
        hourly = amount / hours;
        daily = amount / 5;
        biweekly = amount * 2;
        monthly = (amount * weeks) / 12;
        annual = amount * weeks;
        break;
      case 'biweekly':
        biweekly = amount;
        weekly = amount / 2;
        hourly = weekly / hours;
        daily = weekly / 5;
        monthly = (amount * 26) / 12;
        annual = amount * 26;
        break;
      case 'monthly':
        monthly = amount;
        annual = amount * 12;
        weekly = annual / weeks;
        biweekly = weekly * 2;
        daily = weekly / 5;
        hourly = weekly / hours;
        break;
      case 'annual':
      default:
        annual = amount;
        monthly = amount / 12;
        weekly = amount / weeks;
        biweekly = weekly * 2;
        daily = weekly / 5;
        hourly = weekly / hours;
    }

    setResult({ annual, monthly, biweekly, weekly, daily, hourly });
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Salary Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Salary Calculator</h1>
          <p className="text-gray-400 mb-6">Convert your salary between hourly, daily, weekly, bi-weekly, monthly, and annual amounts.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Salary Amount</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={salary} onChange={(e) => setSalary(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <select value={salaryType} onChange={(e) => setSalaryType(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="hourly">per hour</option>
                    <option value="daily">per day</option>
                    <option value="weekly">per week</option>
                    <option value="biweekly">bi-weekly</option>
                    <option value="monthly">per month</option>
                    <option value="annual">per year</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Hours per Week</label>
                <Input type="number" value={hoursPerWeek} onChange={(e) => setHoursPerWeek(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-32">Weeks per Year</label>
                <Input type="number" value={weeksPerYear} onChange={(e) => setWeeksPerYear(e.target.value)}
                  className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { label: 'Hourly', value: result.hourly },
                    { label: 'Daily', value: result.daily },
                    { label: 'Weekly', value: result.weekly },
                    { label: 'Bi-Weekly', value: result.biweekly },
                    { label: 'Monthly', value: result.monthly },
                    { label: 'Annual', value: result.annual }
                  ].map((item, i) => (
                    <div key={i} className="p-4 bg-[#1a1a1a] rounded-lg">
                      <p className="text-gray-400 text-sm">{item.label}</p>
                      <p className="text-white font-bold text-lg">{formatCurrency(item.value)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Interest', path: '/interest-calculator' }, { name: 'Loan', path: '/loan-calculator' }, { name: 'Sales Tax', path: '/sales-tax-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SalaryCalculatorPage;
